import React from 'react';
import { Leaf, Heart, Users, Scale, Award, Target, Lightbulb, TreePine, Droplet, Recycle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PageTitle from '../hooks/PageTitle';

const SocialResponsibility = () => {
  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Social Responsibility" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <Heart className="h-12 w-12 mr-4" />
              <h1 className="text-5xl md:text-6xl font-serif">
                Social Responsibility & Impact
              </h1>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed">
              Building a sustainable future through environmental action, social inclusion, and community support
            </p>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-6">Our Commitment to Society</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              At Ashbourne Law, we believe that success is measured not only by legal excellence but also by our positive impact on society and the environment. We are committed to creating lasting change through concrete actions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Leaf className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-gray-900 mb-2">Environmental</h3>
                <p className="text-sm text-gray-600">Carbon neutral operations</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Heart className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-gray-900 mb-2">Social Impact</h3>
                <p className="text-sm text-gray-600">1% for children's futures</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-gray-900 mb-2">Inclusion</h3>
                <p className="text-sm text-gray-600">Diverse & inclusive workplace</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Scale className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-gray-900 mb-2">Equality</h3>
                <p className="text-sm text-gray-600">Equal opportunities for all</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Environmental Sustainability */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="flex items-center mb-6">
              <Leaf className="h-10 w-10 text-green-600 mr-4" />
              <h2 className="text-4xl font-serif text-gray-900">Environmental Sustainability</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
              We recognize the urgent need for climate action. Our commitment to environmental sustainability is reflected in concrete, measurable actions across all our operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TreePine className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-serif text-gray-900 mb-3">Carbon Neutrality</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      We have achieved carbon neutrality across all our offices worldwide through renewable energy procurement and verified carbon offset programs.
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-start">
                        <span className="mr-2 text-green-600">✓</span>
                        <span>100% renewable energy in all offices (2023)</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-green-600">✓</span>
                        <span>Carbon offsetting for business travel through reforestation projects</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-green-600">✓</span>
                        <span>Annual carbon footprint audit and reporting</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Recycle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-serif text-gray-900 mb-3">Paperless Operations</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      We've digitized 95% of our operations, significantly reducing paper consumption and waste while improving efficiency.
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-start">
                        <span className="mr-2 text-blue-600">✓</span>
                        <span>Digital-first document management system</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-blue-600">✓</span>
                        <span>E-signatures and digital client communications</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-blue-600">✓</span>
                        <span>98% reduction in paper use since 2020</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Droplet className="h-6 w-6 text-teal-600" />
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-serif text-gray-900 mb-3">Sustainable Office Practices</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      Our offices are designed and operated with environmental responsibility at the forefront.
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-start">
                        <span className="mr-2 text-teal-600">✓</span>
                        <span>LEED-certified office spaces</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-teal-600">✓</span>
                        <span>Comprehensive recycling and composting programs</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-teal-600">✓</span>
                        <span>Water conservation systems and LED lighting</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-serif text-gray-900 mb-3">2030 Sustainability Goals</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      We've set ambitious targets to further reduce our environmental impact by 2030.
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-start">
                        <span className="mr-2 text-purple-600">→</span>
                        <span>Net-zero emissions across entire value chain</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-purple-600">→</span>
                        <span>100% sustainable procurement of office supplies</span>
                      </p>
                      <p className="flex items-start">
                        <span className="mr-2 text-purple-600">→</span>
                        <span>Support 10 major reforestation projects globally</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ashbourne Foundation */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Heart className="h-10 w-10 text-red-600 mr-4" />
                <h2 className="text-4xl font-serif text-gray-900">The Ashbourne Foundation</h2>
              </div>
              <div className="mb-6">
                <Badge className="bg-red-100 text-red-800 text-lg px-4 py-2">1% for Children's Futures</Badge>
              </div>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  We believe in investing in the next generation. The Ashbourne Foundation commits <strong className="text-gray-900">1% of our annual revenues</strong> to programs supporting disadvantaged children worldwide.
                </p>
                <p>
                  Our focus is on education, healthcare, and creating opportunities for children who face systemic barriers to success. Every legal matter we handle contributes to changing young lives.
                </p>
              </div>
            </div>

            <Card className="border-0 shadow-2xl bg-gradient-to-br from-red-50 to-pink-50">
              <CardContent className="p-10">
                <h3 className="text-2xl font-serif text-gray-900 mb-6">Our Goals for the Next 3 Years</h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-red-600 pl-6">
                    <div className="text-4xl font-serif text-gray-900 mb-2">£2.5M+</div>
                    <p className="text-gray-600">Target funding for children's programs</p>
                  </div>
                  <div className="border-l-4 border-red-600 pl-6">
                    <div className="text-4xl font-serif text-gray-900 mb-2">50,000+</div>
                    <p className="text-gray-600">Children to be directly supported</p>
                  </div>
                  <div className="border-l-4 border-red-600 pl-6">
                    <div className="text-4xl font-serif text-gray-900 mb-2">30+</div>
                    <p className="text-gray-600">Countries to reach with our programs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <Lightbulb className="h-10 w-10 text-yellow-600 mb-4" />
                <h3 className="font-serif text-xl text-gray-900 mb-3">Education Access</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Funding scholarships, school infrastructure, and educational technology for underserved communities in London, Africa, and Southeast Asia
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <Heart className="h-10 w-10 text-red-600 mb-4" />
                <h3 className="font-serif text-xl text-gray-900 mb-3">Healthcare Support</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Providing medical care, nutrition programs, and mental health services for children in crisis situations and refugee communities
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <Award className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="font-serif text-xl text-gray-900 mb-3">Skills Development</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Supporting vocational training, mentorship programs, and entrepreneurship initiatives for young people transitioning to adulthood
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Diversity & Inclusion */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="flex items-center mb-6">
              <Users className="h-10 w-10 text-blue-600 mr-4" />
              <h2 className="text-4xl font-serif text-gray-900">Diversity, Inclusion & Equal Opportunity</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
              We are committed to creating a truly inclusive environment where everyone can thrive, regardless of background, identity, or circumstances. Diversity makes us stronger, more innovative, and better equipped to serve our global client base.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-serif text-gray-900 mb-6">Our Commitment to Inclusion</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Gender Equality</h4>
                      <p className="text-sm text-gray-600">48% female partners, equal pay certification, and comprehensive parental leave policies that support all parents equally</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Ethnic Diversity</h4>
                      <p className="text-sm text-gray-600">Active recruitment from underrepresented communities, unconscious bias training, and mentorship programs for diverse talent</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">LGBTQ+ Inclusion</h4>
                      <p className="text-sm text-gray-600">Proud signatory of LGBT Great, active employee resource groups, and inclusive policies across all jurisdictions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Disability Inclusion</h4>
                      <p className="text-sm text-gray-600">Accessible workspaces, assistive technology, and reasonable accommodations to ensure everyone can perform at their best</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Socioeconomic Diversity</h4>
                      <p className="text-sm text-gray-600">Contextual recruitment, social mobility partnerships, and support for first-generation professionals</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-serif text-gray-900 mb-6">Equal Opportunity Initiatives</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Transparent Promotion & Progression</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Clear, objective criteria for advancement with regular performance reviews and development planning for all lawyers
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Pay Equity Guarantee</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Annual pay audits ensure equal compensation for equal work, regardless of gender, ethnicity, or background
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Flexible Working for All</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Hybrid working, flexible hours, and location flexibility available to everyone, supporting work-life integration
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Professional Development</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Equal access to training, mentoring, and leadership development programs with dedicated support for underrepresented groups
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Zero Tolerance Policy</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Strict anti-discrimination and anti-harassment policies with confidential reporting mechanisms and swift action
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-xl bg-blue-50">
            <CardContent className="p-10 text-center">
              <h3 className="text-2xl font-serif text-gray-900 mb-4">Our Diversity Statistics (2024)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                <div>
                  <div className="text-3xl font-serif text-gray-900 mb-2">48%</div>
                  <p className="text-sm text-gray-600">Female Partners</p>
                </div>
                <div>
                  <div className="text-3xl font-serif text-gray-900 mb-2">35%</div>
                  <p className="text-sm text-gray-600">Ethnic Minority Lawyers</p>
                </div>
                <div>
                  <div className="text-3xl font-serif text-gray-900 mb-2">22</div>
                  <p className="text-sm text-gray-600">Languages Spoken</p>
                </div>
                <div>
                  <div className="text-3xl font-serif text-gray-900 mb-2">100%</div>
                  <p className="text-sm text-gray-600">Pay Equity Certified</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif mb-6">Join Us in Making a Difference</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            When you work with Ashbourne Law, you're contributing to a better, more sustainable, and more equitable future
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-gray-900 bg-white rounded-md hover:bg-gray-100 transition-colors"
          >
            Learn More About Our Values
          </a>
        </div>
      </section>
    </div>
  );
};

export default SocialResponsibility;